import React, { useState } from 'react';
import { formatPrice } from '../../utils/format';

interface PaiementModalProps {
  reste: number;
  onValider: (montant: number, mode: string) => void;
  onClose: () => void;
  title?: string;
}

export default function PaiementModal({ reste, onValider, onClose, title }: PaiementModalProps) {
  const [montant, setMontant] = useState<number | ''>('');
  const [modePaiement, setModePaiement] = useState('Espèces');
  const modes = ['Espèces', 'Orange Money', 'Wave'];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!montant || montant <= 0) {
      alert("Veuillez saisir un montant valide.");
      return;
    }
    if (montant > reste) {
      alert("Le montant ne peut pas être supérieur au reste à payer.");
      return;
    }
    onValider(montant, modePaiement);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4 sm:p-6 opacity-100 transition-opacity">
      <div className="bg-white w-full max-w-sm rounded-[24px] shadow-2xl overflow-hidden scale-100 transition-transform flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-black text-gray-900 font-playfair tracking-tight">{title || "Solder une Vente"}</h2>
            <p className="text-xs font-semibold uppercase tracking-widest text-or-500 mt-1">Paiement Partiel</p>
          </div>
          <button 
            onClick={onClose}
            title="Fermer"
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-50 text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
          <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-5 text-center">
             <p className="text-xs font-bold text-red-500 uppercase tracking-widest mb-1 shadow-sm">Reste à payer actuel</p>
             <p className="text-2xl font-black text-red-600">{formatPrice(reste)}</p>
          </div>

          <form id="payment-form" onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Montant versé aujourd'hui</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <i className="ri-money-dollar-circle-line text-gray-400 text-lg" />
                </div>
                <input 
                  type="number" 
                  value={montant}
                  onChange={e => setMontant(e.target.value === '' ? '' : Number(e.target.value))}
                  max={reste}
                  min="0"
                  required
                  placeholder={`Max: ${reste}`}
                  className="w-full pl-11 pr-4 py-3 bg-gray-50 border-2 border-transparent focus:border-terre-500 focus:bg-white rounded-xl text-gray-900 font-bold transition-all outline-none group-hover:border-gray-200"
                />
              </div>
              {montant && typeof montant === 'number' && montant < reste && (
                 <p className="text-xs font-semibold text-gray-400 mt-2">Nouveau reste calculé : <span className="text-red-500">{formatPrice(reste - montant)}</span></p>
              )}
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Mode de paiement</label>
              <div className="grid grid-cols-3 gap-2">
                {modes.map(m => (
                  <button 
                    key={m} 
                    type="button"
                    onClick={() => setModePaiement(m)}
                    className={`py-2 px-1 rounded-xl text-[10px] font-bold border-2 transition-all ${modePaiement === m ? 'bg-terre-50 border-terre-500 text-terre-700 shadow-sm' : 'border-gray-100 text-gray-500 hover:bg-gray-50'}`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 bg-gray-50/50 sticky bottom-0 z-10 flex gap-3">
          <button 
            type="button"
            onClick={onClose}
            className="flex-1 py-3 px-4 bg-white border-2 border-gray-200 hover:bg-gray-50 hover:border-gray-300 text-gray-700 font-bold rounded-xl transition-all text-sm"
          >
            Annuler
          </button>
          <button 
            form="payment-form"
            type="submit"
            className="flex-1 py-3 px-4 bg-terre-500 hover:bg-terre-600 shadow-lg shadow-terre-500/30 text-white font-bold rounded-xl transition-all text-sm flex items-center justify-center gap-2"
          >
            <i className="ri-check-line" />
            Solder
          </button>
        </div>

      </div>
    </div>
  );
}
